import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellOff, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatLongDate } from '@unifemga/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  EmptyState,
  PageHeader,
  Pagination,
  Skeleton,
  type PageMeta,
} from '@/components/ui';

interface Inbox {
  unreadCount: number;
  notifications: Array<{
    id: string;
    event: string;
    subject: string | null;
    body: string;
    readAt: string | null;
    createdAt: string;
  }>;
  meta: PageMeta;
}

/** Tone by what the message is about, so the list is scannable. */
function toneFor(event: string): { dot: string; label: string } {
  if (/OVERDUE|REJECTED|DEFAULTED/.test(event)) return { dot: 'bg-danger', label: 'Action needed' };
  if (/DUE_SOON|DUE_TODAY|CORRECTION|GUARANTOR_REQUESTED/.test(event))
    return { dot: 'bg-warning', label: 'Reminder' };
  if (/APPROVED|DISBURSED|RECEIVED|COMPLETED/.test(event))
    return { dot: 'bg-success', label: 'Update' };
  return { dot: 'bg-info', label: 'Notice' };
}

export default function MemberNotifications() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['inbox', page],
    queryFn: () => api.get<Inbox>(`/notifications/inbox?page=${page}&pageSize=20`),
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/inbox/${id}/read`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['inbox'] }),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const unread = data?.notifications.filter((n) => !n.readAt) ?? [];
      await Promise.all(unread.map((n) => api.post(`/notifications/inbox/${n.id}/read`)));
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['inbox'] }),
  });

  if (isLoading) return <Skeleton className="h-96" />;

  const notifications = data?.notifications ?? [];

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        eyebrow="Your inbox"
        title="Notifications"
        description={
          data?.unreadCount
            ? `${data.unreadCount} unread`
            : 'Everything UNIFEMGA has sent you in the portal.'
        }
        actions={
          data && data.unreadCount > 0 ? (
            <Button variant="outline" loading={markAllRead.isPending} onClick={() => markAllRead.mutate()}>
              <Check size={16} /> Mark all read
            </Button>
          ) : undefined
        }
      />

      {notifications.length === 0 ? (
        <EmptyState
          icon={<BellOff size={22} />}
          title="Nothing here yet"
          description="Updates about your loans, repayments and verification will appear here."
        />
      ) : (
        <div className="space-y-2.5">
          {notifications.map((notification) => {
            const tone = toneFor(notification.event);
            const unread = !notification.readAt;

            return (
              <Card
                key={notification.id}
                className={cn(
                  'transition-colors',
                  unread && 'border-primary/20 bg-primary/[0.03]',
                )}
              >
                <CardContent className="flex gap-3.5 p-4">
                  <span
                    className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', tone.dot)}
                    aria-hidden="true"
                  />

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p
                        className={cn(
                          'text-sm',
                          unread ? 'font-bold text-foreground' : 'font-semibold text-foreground',
                        )}
                      >
                        {notification.subject ?? tone.label}
                      </p>
                      <p className="numeric shrink-0 text-xs text-foreground-secondary">
                        {formatLongDate(new Date(notification.createdAt))}
                      </p>
                    </div>

                    <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-foreground-secondary">
                      {notification.body}
                    </p>

                    {unread && (
                      <button
                        type="button"
                        onClick={() => markRead.mutate(notification.id)}
                        className="mt-2 text-xs font-bold text-primary hover:underline"
                      >
                        Mark as read
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          <Card>
            <Pagination meta={data?.meta} onPageChange={setPage} className="border-t-0" />
          </Card>
        </div>
      )}

      {/* Stated, not offered. Repayment reminders exist to keep a member
          eligible to borrow, so they are not something to opt out of. */}
      <p className="mt-6 text-center text-sm leading-relaxed text-foreground-secondary">
        UNIFEMGA also contacts you by email and SMS about your loans and repayments. Keep your{' '}
        <Link to="/portal/profile" className="font-bold text-primary hover:underline">
          phone number and email
        </Link>{' '}
        up to date so these reach you.
      </p>
    </div>
  );
}
